import { ScientificAnalysisConditionComponent, ScientificAnalysisStaticImplements } from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel/scientific-analysis-filter-panel-conditionfactory'
import { ScientificAnalysisSelectedFilter } from '@/modules/scientific-analysis-module/scientific-analysis-module.models'
import { ScientificAnalysisComparatorType, ScientificAnalysisFieldAdditionalInfo } from '@/modules/scientific-analysis-module/services/scientific-analysis-dtos'
import { ScientificAnalysisComparisonType, ScientificAnalysisCondition, ScientificAnalysisConditionValidationErrorCode } from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import { Vue, Component, Prop } from 'vue-property-decorator'
import PatientIdentifierResource from '@/api/resources/PatientIdentifierResource'
import { initialScientificAnalysisFilterPanelSelectPatientIdentifierState, ScientificAnalysisCenter, ScientificAnalysisFilterPanelSelectPatientIdentifierState, ScientificAnalysisIdentifierType, SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR, SCIENTIFIC_ANALYSIS_NHC_CENTER } from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-selectpatientidentifier/scientific-analysis-filter-panel-selectpatientidentifier.models'
import CenterResource from '@/api/resources/common/commonCenterService'

@Component({
    components: {}
})
// eslint-disable-next-line no-use-before-define
    @ScientificAnalysisStaticImplements<ScientificAnalysisConditionComponent<ScientificAnalysisFilterPanelSelectpatientidentifier>>()
export default class ScientificAnalysisFilterPanelSelectpatientidentifier extends Vue {
    state: ScientificAnalysisFilterPanelSelectPatientIdentifierState
    static COMPONENT_NAME = 'scientific-analysis-filter-panel-selectpatientidentifier'
    static ALLOWED_COMPARATORS: ScientificAnalysisComparatorType[] = [
        ScientificAnalysisComparatorType.CONTAINS,
        ScientificAnalysisComparatorType.ENDS_WITH,
        ScientificAnalysisComparatorType.EQUALS,
        ScientificAnalysisComparatorType.NOT_CONTAINS,
        ScientificAnalysisComparatorType.NOT_EQUAL,
        ScientificAnalysisComparatorType.STARTS_WITH
    ]
    @Prop() condition!: ScientificAnalysisCondition
    @Prop() selectedFilter!: ScientificAnalysisSelectedFilter

    constructor() {
        super()
        this.state = initialScientificAnalysisFilterPanelSelectPatientIdentifierState()
    }

    async mounted(): Promise<void> {
        await this.getIdentifiers()
        await this.getCenters()
        if (this.condition.value.length > 0) {
            const fields = (this.condition.value as string).split(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)
            this.state.identifierSelected = this.state.patientIdentifierOptions.find(type => type.idtId === parseInt(fields[0]))
            if (fields[1] !== '-1') {
                this.state.centerSelected = this.state.centers.find(center => center.id === parseInt(fields[1]))
            }
        }
    }

    static canAccept(comparisonType: ScientificAnalysisComparisonType): boolean {
        return !!comparisonType.additionalInfo && comparisonType.additionalInfo !== null &&
            comparisonType.additionalInfo === ScientificAnalysisFieldAdditionalInfo.selectPatientIdentifier &&
            ScientificAnalysisFilterPanelSelectpatientidentifier.ALLOWED_COMPARATORS.includes(comparisonType.comparatorType)
    }

    get centerSelected(): boolean {
        return !!this.state.identifierSelected && this.state.identifierSelected.code === SCIENTIFIC_ANALYSIS_NHC_CENTER
    }

    async getIdentifiers(): Promise<void> {
        const response = await this.$_toCallSecureLoading(PatientIdentifierResource.index())
        if (response) {
            this.state.patientIdentifierOptions = response
            this.state.identifierSelected = this.$_globalParameters?.mainPatientIdentifierType
        }
    }

    async getCenters():Promise<void> {
        const response = await this.$_toCallSecureLoading(CenterResource.getActiveCenters())
        if (response) {
            this.state.centers = response
            this.state.centerSelected = response[0]
        }
    }

    _onChangeType(change: ScientificAnalysisIdentifierType): void {
        const fields = (this.condition.value as string).split(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)
        if (fields.length >= 3 && change.code !== SCIENTIFIC_ANALYSIS_NHC_CENTER) {
            fields.splice(0, 2)
            this.condition.value = `${change.idtId}${SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR}`
            this.condition.value += fields.join(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)
        } else if (fields.length <= 2 && change.code === SCIENTIFIC_ANALYSIS_NHC_CENTER) {
            this.condition.value = change.idtId + SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR + this.state.centerSelected?.id + SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR + (fields.length > 1 ? fields[1] : '')
        }
    }

    _onChangeCenter(change: ScientificAnalysisCenter): void {
        const fields = (this.condition.value as string).split(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)
        this.condition.value = `${fields[0]}${SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR}`
        fields.splice(0, 2)
        this.condition.value += `${change.id}${SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR}${fields.join(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)}`
    }

    get combinedCondition(): string {
        const numberOfFields = (this.centerSelected ? 3 : 2)
        const fields = (this.condition.value as string).split(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)
        if (fields.length >= numberOfFields) {
            fields.splice(0, numberOfFields - 1)
            return fields.join(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)
        }
        return this.condition.value as string
    }

    set combinedCondition(value: string) {
        this.condition.value = `${this.state.identifierSelected?.idtId}${SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR}`
        this.condition.value += (this.centerSelected ? `${this.state.centerSelected?.id}${SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR}` : ``) + `${value}`
    }

    get _validate(): boolean {
        const numberOfFields = (this.centerSelected ? 3 : 2)
        const fields = (this.condition.value as string).split(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)

        fields.splice(0, numberOfFields - 1)
        const valueField = fields.join(SCIENTIFIC_ANALYSIS_IDENTIFIER_TYPE_FIELD_SEPARATOR)

        if (valueField.length === 0) {
            this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.EMPTY
        } else {
            this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.NONE
        }
        return this.condition.validationErrorCode !== ScientificAnalysisConditionValidationErrorCode.NONE
    }
}
